import { useState } from 'react';
import type { AtHomeFleet } from '@aithora/shared';
import { liveAge } from './OpAlerts';
import type { OpCtx } from './OperatorHome';
import { Act, chip16, D, S } from './ui';
import { BuildLabel, useCustomerText } from '../../ui/buildLabels';

// Devices (design T11934-12058, hOpFleet): ranked by risk to a person, not by device
// inventory. Reads the PROPOSED GET /athome/fleet.

type Row = AtHomeFleet['members'][number] & { ok: number; total: number; down: string[]; lows: AtHomeFleet['members'][number]['devices']; state: 'none' | 'partial' | 'degrading' | 'ok' };
const ORDER = { none: 0, partial: 1, degrading: 2, ok: 3 } as const;
const HEAD = 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;';
const barCss = (w: number, col: string) => 'display:block;height:100%;width:' + w + '%;border-radius:2px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;background:' + col + ';';

export function OpDevices({ c }: { c: OpCtx }) {
  const plain = useCustomerText();
  const f = c.o.fleet.data;
  const [openId, setOpenId] = useState<string | null>(null);
  const open = (r: Row) => c.viewAs({ residentId: r.residentId, name: r.name });
  const rows: Row[] = (f?.members ?? []).map((m) => {
    const ok = m.devices.filter((d) => d.reporting).length;
    const down = m.devices.filter((d) => !d.reporting).map((d) => d.kind);
    // A battery the device link does not report is unknown, never low.
    const lows = m.devices.filter((d) => d.reporting && d.battery != null && d.battery < 20);
    return { ...m, ok, total: m.devices.length, down, lows, state: ok === 0 ? 'none' : down.length ? 'partial' : lows.length ? 'degrading' : 'ok' };
  });
  const none = rows.filter((r) => r.state === 'none');
  const partial = rows.filter((r) => r.state === 'partial');
  const deg = rows.filter((r) => r.state === 'degrading');
  const okRows = rows.filter((r) => r.state === 'ok');
  const untested = rows.filter((r) => r.neverTested);
  const work = none.concat(partial.slice().sort((a, b) => (b.darkMinutes ?? 0) - (a.darkMinutes ?? 0)));
  const allDev = rows.reduce((n, r) => n + r.total, 0);
  const covPct = allDev ? Math.round((rows.reduce((n, r) => n + r.ok, 0) / allDev) * 100) : 0;

  return (
    <D css="display:flex;flex-direction:column;gap:9px;min-width:0;">
      <D css="display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:5px;min-width:0;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.12);">
        <S css={'flex:0 0 auto;font-size:21px;font-weight:700;letter-spacing:-.025em;line-height:1.15;color:' + (none.length ? '#B3261E' : partial.length ? '#4A5866' : '#12503C') + ';'}>
          {!f
            ? 'Device cover is not connected yet'
            : none.length
              ? `${none.length}${none.length === 1 ? ' person has no cover at all' : ' people have no cover at all'}`
              : partial.length
                ? `${partial.length}${partial.length === 1 ? ' person is down to a partial safety path' : ' people are down to a partial safety path'}`
                : 'Every member has a full safety path'}
        </S>
        <S css="flex:1 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;">
          {f
            ? `${covPct}% of ${allDev} devices reporting across ${rows.length} members · ${deg.length} degrading · ${untested.length} never tested. A silent device is a person losing cover, not a data-quality problem.`
            : plain('Waiting on GET /athome/fleet. Nothing here means not wired, never all covered.')}
        </S>
        {f && !f.real && <BuildLabel><S css={chip16('#54606C', 'rgba(34,39,46,.09)')}>SAMPLE</S></BuildLabel>}
      </D>

      {work.length > 0 && (
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD + 'color:#B3261E;'}>Losing cover</S>
            <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#B3261E;">{String(work.length)}</S>
            <S css="flex:1 1 170px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">worst first {'·'} clocks are live {'·'} open a row to see the devices</S>
          </D>
          {work.map((r) => {
            const bad = r.state === 'none';
            const col = bad ? '#B3261E' : '#5B6B7C';
            const isOpen = openId === r.residentId;
            const dark = liveAge((r.darkMinutes ?? 0) * 60_000);
            return (
              <D key={r.residentId} css="margin-bottom:5px;min-width:0;">
                <D css={'display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;padding:8px 11px;border-radius:10px;min-width:0;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);background:' + (bad ? 'rgba(179,38,30,.06)' : 'rgba(91,107,124,.07)') + ';border:1px solid ' + (bad ? 'rgba(179,38,30,.34)' : 'rgba(91,107,124,.3)') + ';'}>
                  <S css={'flex:0 0 9px;width:9px;height:9px;border-radius:50%;background:' + col + ';animation:' + (bad ? 'pRed 1.6s ease-out infinite' : 'pAmb 2.4s ease-out infinite') + ';'} />
                  <S css="flex:1 1 150px;min-width:0;">
                    <S css="display:block;font-size:16px;font-weight:700;letter-spacing:-.015em;overflow-wrap:break-word;">{r.name}</S>
                    <S css="display:block;font-size:14px;color:#54606C;">{r.place ?? ''}</S>
                  </S>
                  <S css={chip16(col, bad ? 'rgba(179,38,30,.13)' : 'rgba(91,107,124,.17)')}>{bad ? 'NO COVER' : 'PARTIAL COVER'}</S>
                  <S css="flex:2 1 220px;min-width:0;font-size:14px;color:#22272E;line-height:1.35;text-wrap:pretty;">{`${r.down.join(' + ')} silent · ${bad ? 'nothing else reporting' : `${r.ok} of ${r.total} still reporting`}`}</S>
                  <S css="flex:0 0 auto;text-align:right;">
                    <S css={'display:block;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1;color:' + col + ';'}>{dark}</S>
                    <S css="display:block;font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#54606C;margin-top:2px;">dark for</S>
                  </S>
                  <S css={chip16(r.owner ? '#3C454F' : '#B3261E', r.owner ? 'rgba(34,39,46,.08)' : 'rgba(179,38,30,.12)')}>{r.owner ?? 'UNOWNED'}</S>
                  <Act css="flex:0 0 auto;display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:8px;border:1px solid rgba(34,39,46,.22);font-size:14px;font-weight:700;text-decoration:none;color:#3C454F;white-space:nowrap;" onClick={() => setOpenId(isOpen ? null : r.residentId)}>
                    {isOpen ? 'Hide devices' : `${r.total} devices`}
                  </Act>
                  <Act css="flex:0 0 auto;display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#0F6B63;white-space:nowrap;" onClick={() => open(r)}>
                    Open
                  </Act>
                </D>
                {isOpen && (
                  <D css="margin:0 0 0 18px;padding:5px 11px 6px;border-left:2px solid rgba(34,39,46,.14);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);">
                    {r.devices.map((v, i) => (
                      <S key={i} css="display:flex;align-items:center;gap:9px;padding:2px 0;flex-wrap:wrap;row-gap:3px;min-width:0;">
                        <S css="flex:0 0 96px;font-size:14px;font-weight:700;">{v.kind}</S>
                        <S css="flex:1 1 120px;min-width:0;font-size:14px;color:#3C454F;overflow-wrap:break-word;">{v.make}</S>
                        <S css={chip16(v.reporting ? '#1F6B52' : '#B3261E', v.reporting ? 'rgba(47,138,111,.14)' : 'rgba(179,38,30,.12)')}>{v.reporting ? 'reporting' : 'silent'}</S>
                        <S css="flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;white-space:nowrap;">{v.reporting ? `${v.lastSeenMinutes ?? 0}m ago` : dark}</S>
                        <S css="flex:0 0 40px;height:5px;border-radius:3px;background:rgba(34,39,46,.1);overflow:hidden;"><S css={barCss(v.battery ?? 0, v.battery == null ? '#54606C' : v.battery < 20 ? '#B3261E' : v.battery < 40 ? '#5B6B7C' : '#2F8A6F')} /></S>
                        <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (v.battery == null ? '#54606C' : v.battery < 20 ? '#B3261E' : v.battery < 40 ? '#4A5866' : '#3C454F') + ';'}>{v.battery == null ? 'battery not reported' : `${v.battery}%`}</S>
                        <S css={'flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:' + (v.firmware == null || v.firmware === f!.latestFirmware ? '#54606C' : '#4A5866') + ';'}>{v.firmware == null ? 'fw not reported' : `fw ${v.firmware}`}</S>
                      </S>
                    ))}
                  </D>
                )}
              </D>
            );
          })}
        </D>
      )}
      {f && work.length === 0 && (
        <D css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;padding:8px 12px;border-radius:9px;background:rgba(47,138,111,.09);border:1px solid rgba(47,138,111,.28);font-size:16px;font-weight:600;color:#12503C;min-width:0;">
          <S css="flex:0 0 auto;width:8px;height:8px;border-radius:2px;background:#2F8A6F;animation:breathe 2.6s ease-in-out infinite;" />
          Every member has a full safety path right now.
        </D>
      )}

      <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;padding-bottom:6px;">
          <S css={HEAD + 'color:#22272E;'}>Cover, hour by hour {'·'} last 24h</S>
          <S css="flex:1 1 150px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">one row per member, one cell per hour {'·'} hover a cell for that hour</S>
          {([['full cover', 'rgba(47,138,111,.55)'], ['partial', '#5B6B7C'], ['none', '#B3261E']] as const).map(([k, col]) => (
            <S key={k} css="flex:0 0 auto;display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;white-space:nowrap;">
              <S css={'display:inline-block;width:9px;height:9px;border-radius:2px;background:' + col + ';'} />
              {k}
            </S>
          ))}
        </D>
        {rows
          .slice()
          .sort((a, b) => ORDER[a.state] - ORDER[b.state])
          .map((r, ri) => (
            <Act key={r.residentId} css="display:flex;align-items:center;gap:9px;padding:1px 3px;border-radius:5px;min-width:0;text-decoration:none;color:inherit;" onClick={() => open(r)}>
              <S css={'flex:0 0 116px;font-size:14px;font-weight:600;overflow-wrap:break-word;color:' + (r.state === 'none' ? '#B3261E' : r.state === 'partial' ? '#4A5866' : '#22272E') + ';'}>{r.name}</S>
              <S css="flex:1 1 0;min-width:0;display:flex;align-items:flex-end;gap:2px;">
                {r.hours.map((s, h) => {
                  const ago = r.hours.length - 1 - h;
                  return (
                    <S
                      key={h}
                      title={`${r.name} · ${ago === 0 ? 'this hour' : `${ago}h ago`} · ${s === 2 ? 'full cover' : s === 1 ? 'partial cover' : 'no cover'}`}
                      css={'flex:1 1 0;min-width:3px;height:15px;border-radius:2px;transform-origin:bottom;animation:growH .55s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + (ri * 12 + h * 4) + 'ms;background:' + (s === 2 ? 'rgba(47,138,111,.55)' : s === 1 ? '#5B6B7C' : '#B3261E') + ';'}
                    />
                  );
                })}
              </S>
              <S css={'flex:0 0 34px;text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (r.ok === r.total ? '#1F6B52' : r.ok ? '#4A5866' : '#B3261E') + ';'}>{`${r.total ? Math.round((r.ok / r.total) * 100) : 0}%`}</S>
            </Act>
          ))}
        <S css="display:flex;justify-content:space-between;font-size:14px;color:#54606C;margin:3px 40px 0 125px;">
          <span>24h ago</span><span>18h</span><span>12h</span><span>6h</span><span>now</span>
        </S>
      </D>

      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px 22px;min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);align-items:start;">
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD + 'color:#4A5866;'}>Degrading</S>
            <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#4A5866;">{String(deg.length)}</S>
            <S css="flex:1 1 130px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">covered, will not stay that way</S>
          </D>
          {deg
            .slice()
            .sort((a, b) => (a.lows[0].battery ?? 0) - (b.lows[0].battery ?? 0))
            .map((r) => {
              const low = r.lows[0];
              const pct = low.battery ?? 0;
              const red = pct < 12;
              return (
                <Act key={r.residentId} css="display:block;box-sizing:border-box;padding:6px 10px;margin-bottom:4px;border-radius:9px;background:rgba(91,107,124,.07);border:1px solid rgba(91,107,124,.28);text-decoration:none;color:inherit;min-width:0;" onClick={() => open(r)}>
                  <S css="display:flex;align-items:baseline;gap:7px;min-width:0;">
                    <S css="flex:1 1 0;min-width:0;font-size:16px;font-weight:700;overflow-wrap:break-word;">{r.name}</S>
                    <S css={'flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (red ? '#B3261E' : '#4A5866') + ';'}>{`${pct}%`}</S>
                  </S>
                  <S css="display:block;font-size:14px;color:#3C454F;line-height:1.3;margin-top:1px;text-wrap:pretty;">{`${low.kind} battery · ${red ? 'hours, not days' : 'replace this week'}`}</S>
                  <S css="display:block;height:4px;border-radius:2px;background:rgba(34,39,46,.1);margin-top:5px;overflow:hidden;"><S css={barCss(pct, red ? '#B3261E' : '#5B6B7C')} /></S>
                </Act>
              );
            })}
        </D>
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD + 'color:#1F6B52;'}>Fully covered</S>
            <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#1F6B52;">{String(okRows.length)}</S>
            <S css="flex:1 1 130px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">every device reporting {'·'} tap a name to open that member</S>
          </D>
          <D css="display:flex;flex-wrap:wrap;gap:4px;min-width:0;">
            {okRows.map((r) => {
              const parts = r.name.split(' ');
              return (
                <Act key={r.residentId} title={`${r.name} · ${r.ok} of ${r.total} devices reporting`} css="display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:20px;background:rgba(47,138,111,.10);border:1px solid rgba(47,138,111,.26);font-size:14px;font-weight:600;text-decoration:none;color:#12503C;white-space:nowrap;" onClick={() => open(r)}>
                  <S css="flex:0 0 6px;width:6px;height:6px;border-radius:50%;background:#2F8A6F;" />
                  {parts[1] ? `${parts[0]} ${parts[1].charAt(0)}.` : r.name}
                </Act>
              );
            })}
          </D>
        </D>
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD + 'color:#54606C;'}>Never tested</S>
            <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#54606C;">{String(untested.length)}</S>
            <S css="flex:1 1 130px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">the ladder has never run. Unknown, not healthy.</S>
          </D>
          <D css="display:flex;flex-wrap:wrap;gap:5px;min-width:0;">
            {untested.map((r) => (
              <Act key={r.residentId} css="display:inline-flex;align-items:baseline;gap:6px;padding:3px 10px;border-radius:20px;border:1px dashed rgba(34,39,46,.32);font-size:14px;font-weight:600;text-decoration:none;color:#3C454F;white-space:nowrap;" onClick={() => open(r)}>
                {r.name}
                <S css="font-size:14px;font-weight:400;color:#54606C;">{r.neverTested ?? ''}</S>
              </Act>
            ))}
          </D>
        </D>
      </D>
      <D css="font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;">{`Ranked by risk to a person, not by device inventory. No cover means nothing is reporting. Partial means one path is dark but another still works. ${f && !f.real ? 'Battery, firmware and dark-time are MODELED from the sample feed; the dark-time clocks are counting live.' : 'The dark-time clocks are counting live.'} Never tested means the escalation ladder has never actually run for that member. We do not know that it works.`}</D>
    </D>
  );
}
